// src/components/VehicleArchiveList.jsx
import { useEffect, useState, useMemo } from 'react';
import {
  Table,
  ScrollArea,
  Badge,
  Group,
  Text,
  ActionIcon,
  LoadingOverlay,
  Paper,
  Button,
  Tooltip,
  Modal,
  TextInput,
  Stack,
  Flex,
  ThemeIcon,
  Divider,
  UnstyledButton,
  SimpleGrid,
  Title,
  Box,
  Center,
} from '@mantine/core';
import {
  IconEye,
  IconSearch,
  IconCar,
  IconInfoCircle,
  IconDownload,
  IconSelector,
  IconChevronUp,
  IconChevronDown,
  IconArchive,
  IconBuildingStore,
  IconArrowBackUp,
  IconTrashX,
  IconFileText,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import api from '../../api/axiosConfig';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { downloadVehicleExcel } from './utils/vehicleExcelDownload';

import VehicleDetailModal from '../../components/VehicleMaintenanceTracking/VehicleDetailModal';
// Eğer hata alırsanız import yolunu "import VehicleDocumentsModal from './VehicleDocumentsModal';" olarak değiştirin
import VehicleDocumentsModal from '../../components/VehicleMaintenanceTracking/VehicleDocumentsModal';

export default function VehicleArchiveList() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);

  const [globalSearch, setGlobalSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'archived_at', direction: 'desc' });

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleToRestore, setVehicleToRestore] = useState(null);

  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);
  const [restoreModalOpened, { open: openRestoreModal, close: closeRestoreModal }] = useDisclosure(false);
  const [docsModalOpened, { open: openDocsModal, close: closeDocsModal }] = useDisclosure(false);

  const fetchArchivedVehicles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/vehicles/archive/');
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Arşiv listesi çekilemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedVehicles();
  }, []);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      const searchTerm = globalSearch.toLocaleLowerCase('tr-TR');
      return searchTerm
        ? vehicle.plate?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.brand?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.archive_reason?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.commercial_type?.toLocaleLowerCase('tr-TR').includes(searchTerm)
        : true;
    });
  }, [vehicles, globalSearch]);

  const sortedVehicles = useMemo(() => {
    const sortableItems = [...filteredVehicles];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (!aValue) return 1;
        if (!bValue) return -1;

        const dateA = dayjs(aValue).valueOf();
        const dateB = dayjs(bValue).valueOf();

        if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredVehicles, sortConfig]);

  const summaryStats = useMemo(() => {
    const total = vehicles.length;
    const sold = vehicles.filter((v) => v.archive_reason === 'Satıldı').length;
    const scrapped = vehicles.filter((v) => v.archive_reason === 'Hurdaya Ayrıldı').length;
    const others = total - sold - scrapped;

    return { total, sold, scrapped, others };
  }, [vehicles]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (date) => (date ? dayjs(date).format('DD.MM.YYYY') : '-');

  const getReasonColor = (reason) => {
    if (reason === 'Satıldı') return 'teal';
    if (reason === 'Hurdaya Ayrıldı') return 'red';
    if (reason === 'Kiralama Süresi Bitti') return 'orange';
    return 'gray';
  };

  // Veriler artık tarayıcıda yeniden Excel'e dönüştürülmüyor.
  // Raporun tamamı backend Tasarım V2 endpointinden indiriliyor (arama filtresinden bağımsız).
  const handleExportToExcel = async () => {
    setExportingExcel(true);
    try {
      await downloadVehicleExcel('archive');
      notifications.show({ title: 'Excel indiriliyor', message: 'Tüm araç arşivi - Tasarım V2', color: 'teal' });
    } catch (error) {
      notifications.show({ title: 'Excel indirilemedi', message: error.message, color: 'red' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleRestoreClick = (vehicle) => {
    setVehicleToRestore(vehicle);
    openRestoreModal();
  };

  const confirmRestore = async () => {
    if (!vehicleToRestore) return;
    try {
      await api.put(`/vehicles/${vehicleToRestore.id}/restore`);
      await fetchArchivedVehicles();
      closeRestoreModal();
      setVehicleToRestore(null);
    } catch (error) {
      console.error('Geri yükleme hatası', error);
      alert('İşlem sırasında bir hata oluştu.');
    }
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    openDetailModal();
  };

  const handleDocsClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    openDocsModal();
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <IconSelector size={14} color="gray" />;
    return sortConfig.direction === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
  };

  const rows = sortedVehicles.map((vehicle) => (
    <Table.Tr key={vehicle.id} style={{ color: 'var(--mantine-color-dimmed)' }}>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>
        <Badge size="lg" variant="outline" color="gray" radius="md" fw={800}>
          {vehicle.plate}
        </Badge>
      </Table.Td>

      <Table.Td>
        <Stack gap={2}>
          <Text size="sm" fw={700} c="dark">{vehicle.brand || '-'}</Text>
          <Text size="xs">{vehicle.commercial_type || '-'}</Text>
        </Stack>
      </Table.Td>

      <Table.Td>
        <Text size="sm" fw={500}>{vehicle.model_year || '-'}</Text>
      </Table.Td>

      <Table.Td>
        <Badge color={vehicle.ownership === 'Öz Mal' ? 'teal' : 'indigo'} variant="dot" radius="xl">
          {vehicle.ownership || '-'}
        </Badge>
      </Table.Td>

      <Table.Td style={{ textAlign: 'right' }}>
        <Text size="sm" fw={700}>{vehicle.current_km ? `${vehicle.current_km.toLocaleString()} km` : '-'}</Text>
      </Table.Td>

      <Table.Td>
        <Text size="sm" fw={600}>{formatDate(vehicle.archived_at)}</Text>
      </Table.Td>

      <Table.Td>
        <Badge color={getReasonColor(vehicle.archive_reason)} variant="light" size="md">
          {vehicle.archive_reason || 'Diğer'}
        </Badge>
      </Table.Td>

      <Table.Td>
        <Group gap={6} justify="flex-end" wrap="nowrap">
          <Tooltip label="Detayları Gör" withArrow position="top">
            <ActionIcon variant="light" color="gray" radius="xl" size="lg" onClick={() => handleViewDetails(vehicle)}>
              <IconEye size={17} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Belgeler" withArrow position="top">
            <ActionIcon
              variant="light"
              color="violet"
              radius="xl"
              size="lg"
              onClick={() => handleDocsClick(vehicle)}
            >
              <IconFileText size={17} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Aktif Filoya Geri Yükle" withArrow position="top">
            <ActionIcon variant="light" color="blue" radius="xl" size="lg" onClick={() => handleRestoreClick(vehicle)}>
              <IconArrowBackUp size={17} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="lg" pos="relative" style={{ minHeight: '70vh' }}>
      <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />

      {/* HERO: ARŞİV KONSEPTİ (Koyu Slate/Gray Tonları) */}
      <Paper
        radius="xl"
        p={{ base: 'lg', md: 'xl' }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.96) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
        }}
      >
        <Box style={{ position: 'absolute', top: -70, right: -70, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} wrap="wrap" gap="md" style={{ position: 'relative', zIndex: 1 }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon size={54} radius="xl" variant="black" color="rgba(255,255,255,0.08)">
              <IconArchive size={28} color="white" />
            </ThemeIcon>
            <div>
              <Text fw={700} size="sm" c="rgba(255,255,255,0.5)">Geçmiş Veritabanı</Text>
              <Title order={2} c="white" fw={900} style={{ letterSpacing: '-0.7px' }}>Araç Arşivi</Title>
              <Text size="sm" maw={720} style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
                Satılan, hurdaya ayrılan veya kiralama süresi biten araçların listesi. Bu araçlar aktif hesaplamalara dahil edilmez.
              </Text>
            </div>
          </Group>
        </Flex>
      </Paper>

      {/* ÖZET KARTLAR */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        <Paper radius="xl" p="lg" withBorder bg="gray.0">
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>Toplam Arşivlenen</Text>
              <Title order={2} fw={900}>{summaryStats.total}</Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="gray"><IconArchive size={26} /></ThemeIcon>
          </Group>
        </Paper>

        <Paper radius="xl" p="lg" withBorder bg="gray.0">
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>Satılan Araçlar</Text>
              <Title order={2} fw={900}>{summaryStats.sold}</Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="teal"><IconBuildingStore size={26} /></ThemeIcon>
          </Group>
        </Paper>

        <Paper radius="xl" p="lg" withBorder bg="gray.0">
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>Hurdaya Ayrılan</Text>
              <Title order={2} fw={900}>{summaryStats.scrapped}</Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="red"><IconTrashX size={26} /></ThemeIcon>
          </Group>
        </Paper>

        <Paper radius="xl" p="lg" withBorder bg="gray.0">
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>Diğer / Kira Bitişi</Text>
              <Title order={2} fw={900}>{summaryStats.others}</Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="orange"><IconCar size={26} /></ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* FİLTRE VE LİSTE */}
      <Paper radius="xl" withBorder p="md" style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.03)' }}>
        <Stack gap="md">
          <Flex justify="space-between" align="center" wrap="wrap" gap="md">
            <div>
              <Title order={4} fw={800}>Arşiv Listesi</Title>
              <Text size="sm" c="dimmed">Sistemdeki pasif araçlar</Text>
            </div>
            <Group>
              <Button variant="light" color="green" leftSection={<IconDownload size={18} />} onClick={handleExportToExcel} radius="xl" loading={exportingExcel} disabled={vehicles.length === 0}>
                Tüm Arşivi Excel İndir (V2)
              </Button>
            </Group>
          </Flex>

          <Divider />

          <TextInput
            placeholder="Plaka, marka veya arşiv sebebi ara..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            radius="xl"
            maw={400}
          />
        </Stack>
      </Paper>

      {/* GERİ YÜKLEME MODALI */}
      <Modal
        opened={restoreModalOpened}
        onClose={closeRestoreModal}
        centered
        radius="xl"
        overlayProps={{ blur: 3 }}
        title={
          <Group gap="sm">
            <ThemeIcon color="blue" variant="light" radius="xl" size="md">
              <IconArrowBackUp size={18} />
            </ThemeIcon>
            <Text fw={800} size="lg">Geri Yükleme Onayı</Text>
          </Group>
        }
      >
        <Stack gap="lg">
          <Paper p="md" radius="lg" bg="blue.0" style={{ border: '1px solid var(--mantine-color-blue-2)' }}>
            <Text size="sm">
              <b>{vehicleToRestore?.plate}</b> plakalı aracı arşivden çıkarıp tekrar aktif filoya dahil etmek istediğinize emin misiniz?
            </Text>
          </Paper>
          <Group justify="flex-end">
            <Button variant="default" radius="xl" onClick={closeRestoreModal}>Vazgeç</Button>
            <Button color="blue" radius="xl" onClick={confirmRestore} leftSection={<IconArrowBackUp size={16} />}>
              Evet, Geri Yükle
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* DETAY MODALI */}
      <VehicleDetailModal
        opened={detailModalOpened}
        close={closeDetailModal}
        vehicle={selectedVehicle}
      />

      {/* BELGELER MODALI */}
      <VehicleDocumentsModal
        opened={docsModalOpened}
        close={closeDocsModal}
        vehicle={selectedVehicle}
      />

      {/* TABLO */}
      <Paper radius="xl" withBorder style={{ overflow: 'hidden' }}>
        <ScrollArea h="55vh" type="auto" offsetScrollbars>
          <Table stickyHeader striped highlightOnHover verticalSpacing="md" horizontalSpacing="md" style={{ minWidth: 1000 }}>
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Plaka</Table.Th>
                <Table.Th>Marka / Tip</Table.Th>
                <Table.Th>Model</Table.Th>
                <Table.Th>Mülkiyet</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Son Kilometre</Table.Th>
                <Table.Th>
                  <UnstyledButton onClick={() => requestSort('archived_at')} p={4} style={{ borderRadius: 6 }}>
                    <Group gap={5}><Text size="sm" fw={700}>Arşiv Tarihi</Text><SortIcon columnKey="archived_at" /></Group>
                  </UnstyledButton>
                </Table.Th>
                <Table.Th>Arşiv Sebebi</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>

          {sortedVehicles.length === 0 && !loading && (
            <Center py={70}>
              <Stack align="center" gap="sm">
                <ThemeIcon size={72} radius="xl" variant="light" color="gray"><IconInfoCircle size={34} /></ThemeIcon>
                <Title order={4}>Arşivde araç bulunamadı</Title>
              </Stack>
            </Center>
          )}
        </ScrollArea>
      </Paper>
    </Stack>
  );
}